import React, { useState, memo } from 'react';
import { Sparkles, Clock, BookOpen, Play, Calendar, ArrowRight, Film, Image as ImageIcon, Map, Lock, Search, Filter, Grid, List, SlidersHorizontal } from 'lucide-react';
type Storybook = {
  id: number;
  title: string;
  date: string;
  coverImage: string;
  summary: string;
  storyTitle: string;
  pages: number;
  views?: number;
  height?: 'short' | 'medium' | 'tall';
};
const completedStorybooks: Storybook[] = [{
  id: 1,
  title: 'How Mom and Dad Met',
  date: 'Dec 18, 2024',
  coverImage: 'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=800&h=600&fit=crop',
  summary: 'A heartwarming love story about two college students who met at a dance in 1985. From their first awkward conversation to their first date at the local diner, this is the story of how true love begins.',
  storyTitle: 'How Mom and Dad Met',
  pages: 12,
  views: 45,
  height: 'tall'
}, {
  id: 2,
  title: "Grandma's Apple Pie",
  date: 'Dec 15, 2024',
  coverImage: 'https://images.unsplash.com/photo-1618336753974-aae8e04506aa?w=800&h=600&fit=crop',
  summary: "A treasured family recipe passed down through four generations. Learn the secret ingredient that makes Grandma Rose's apple pie the best in town, along with memories of baking together.",
  storyTitle: "Grandma's Apple Pie Recipe",
  pages: 8,
  views: 32,
  height: 'short'
}, {
  id: 3,
  title: 'Yellowstone Adventure',
  date: 'Dec 10, 2024',
  coverImage: 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&h=600&fit=crop',
  summary: "An unforgettable family road trip through Yellowstone National Park. Witness the majesty of Old Faithful, encounter wild bears, and experience the beauty of America's first national park.",
  storyTitle: 'Road Trip to Yellowstone',
  pages: 16,
  views: 28,
  height: 'medium'
}, {
  id: 4,
  title: 'Summers at Lake Tahoe',
  date: 'Dec 5, 2024',
  coverImage: 'https://images.unsplash.com/photo-1513569771920-c9e1d31714af?w=800&h=600&fit=crop',
  summary: 'Childhood memories of magical summers spent at the family lake house. Fishing with Grandpa at dawn, swimming until sunset, and gathering around the campfire for stories under the stars.',
  storyTitle: 'Summer at Lake Tahoe',
  pages: 10,
  views: 51,
  height: 'short'
}, {
  id: 5,
  title: 'Max Joins the Family',
  date: 'Nov 28, 2024',
  coverImage: 'https://images.unsplash.com/photo-1614732414444-096e5f1122d5?w=800&h=600&fit=crop',
  summary: 'The heartwarming tale of how a golden retriever named Max came into our lives. From his first day at the shelter to becoming the most beloved member of the family.',
  storyTitle: 'The Day We Adopted Max',
  pages: 8,
  views: 38,
  height: 'tall'
}, {
  id: 6,
  title: "Dad's Navy Years",
  date: 'Nov 22, 2024',
  coverImage: 'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=800&h=600&fit=crop',
  summary: 'Stories from the high seas during the 1970s. Experience life aboard a Navy ship, visits to exotic ports around the world, and the brotherhood formed between sailors.',
  storyTitle: "Dad's Navy Stories",
  pages: 20,
  views: 19,
  height: 'medium'
}, {
  id: 7,
  title: 'Our Christmas Traditions',
  date: 'Nov 18, 2024',
  coverImage: 'https://images.unsplash.com/photo-1618336753974-aae8e04506aa?w=800&h=600&fit=crop',
  summary: 'A celebration of family Christmas traditions passed through generations. From decorating the tree together to baking sugar cookies and attending midnight mass on Christmas Eve.',
  storyTitle: 'Christmas Traditions',
  pages: 12,
  views: 67,
  height: 'tall'
}];
type CreationsPageProps = {
  onViewStorybook?: (storybookId: number) => void;
};
type CreationType = 'storybook' | 'movie' | 'image' | 'quest';
type ViewMode = 'grid' | 'list';
type SortOption = 'date' | 'title' | 'views';
export function CreationsPage({
  onViewStorybook
}: CreationsPageProps) {
  const [selectedType, setSelectedType] = useState<CreationType>('storybook');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('date');
  const [showFilters, setShowFilters] = useState(false);
  const creationTypes = [{
    id: 'storybook' as const,
    name: 'Storybooks',
    icon: BookOpen,
    description: 'AI-illustrated books',
    color: 'purple',
    available: true,
    count: completedStorybooks.length
  }, {
    id: 'movie' as const,
    name: 'Movies',
    icon: Film,
    description: 'Animated videos',
    color: 'blue',
    available: false,
    count: 0
  }, {
    id: 'image' as const,
    name: 'Images',
    icon: ImageIcon,
    description: 'Single illustrations',
    color: 'green',
    available: false,
    count: 0
  }, {
    id: 'quest' as const,
    name: 'Quests',
    icon: Map,
    description: 'Adventure journeys',
    color: 'amber',
    available: false,
    count: 0
  }];
  // Filter and sort storybooks
  let filteredStorybooks = completedStorybooks.filter(book => book.title.toLowerCase().includes(searchQuery.toLowerCase()) || book.summary.toLowerCase().includes(searchQuery.toLowerCase()) || book.storyTitle.toLowerCase().includes(searchQuery.toLowerCase()));
  // Sort
  filteredStorybooks = [...filteredStorybooks].sort((a, b) => {
    switch (sortBy) {
      case 'title':
        return a.title.localeCompare(b.title);
      case 'views':
        return (b.views || 0) - (a.views || 0);
      case 'date':
      default:
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    }
  });
  return <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen" data-id="element-573">
      <div className="px-8 py-8" data-id="element-574">
        {/* Creation Type Selector */}
        <div className="mb-8" data-id="element-575">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-id="element-576">Creations</h1>
          <p className="text-gray-600 text-lg mb-6" data-id="element-577">
            Your AI-generated content library
          </p>

          <div className="grid grid-cols-4 gap-4" data-id="element-578">
            {creationTypes.map(type => {
            const Icon = type.icon;
            const isSelected = selectedType === type.id;
            const isAvailable = type.available;
            return <button key={type.id} onClick={() => isAvailable && setSelectedType(type.id)} disabled={!isAvailable} className={`relative p-6 rounded-2xl border-2 transition-all ${isAvailable ? isSelected ? `border-${type.color}-500 bg-${type.color}-50 shadow-lg` : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md' : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'}`} data-id="element-579">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${isAvailable ? isSelected ? `bg-${type.color}-500` : `bg-${type.color}-100` : 'bg-gray-200'}`} data-id="element-580">
                    <Icon className={`w-6 h-6 ${isAvailable ? isSelected ? 'text-white' : `text-${type.color}-600` : 'text-gray-400'}`} data-id="element-581" />
                  </div>
                  <div className="text-left" data-id="element-582">
                    <div className="font-semibold text-gray-900 mb-1" data-id="element-583">
                      {type.name}
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-584">
                      {type.description}
                    </div>
                  </div>

                  {!isAvailable && <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-gray-200 rounded-full" data-id="element-585">
                      <Lock className="w-3 h-3 text-gray-600" data-id="element-586" />
                      <span className="text-xs font-medium text-gray-600" data-id="element-587">
                        Soon
                      </span>
                    </div>}
                </button>;
          })}
          </div>
        </div>

        {/* Storybook Content */}
        {selectedType === 'storybook' && <>
            {/* Search, Filter, View Switcher Bar */}
            <div className="bg-white rounded-2xl p-4 shadow-sm mb-6" data-id="element-588">
              <div className="flex items-center gap-4" data-id="element-589">
                {/* Search */}
                <div className="flex-1 relative" data-id="element-590">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-591" />
                  <input type="text" placeholder="Search storybooks..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors" data-id="element-592" />
                </div>

                {/* Sort Dropdown */}
                <select value={sortBy} onChange={e => setSortBy(e.target.value as SortOption)} className="px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors font-medium text-gray-700" data-id="element-593">
                  <option value="date" data-id="element-594">Sort by Date</option>
                  <option value="title" data-id="element-595">Sort by Title</option>
                  <option value="views" data-id="element-596">Sort by Views</option>
                </select>

                {/* View Mode Toggle */}
                <div className="flex items-center gap-2 bg-gray-50 p-1 rounded-xl" data-id="element-597">
                  <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} title="Grid view" data-id="element-598">
                    <Grid className="w-5 h-5" data-id="element-599" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} title="List view" data-id="element-600">
                    <List className="w-5 h-5" data-id="element-601" />
                  </button>
                </div>
              </div>
            </div>

            {/* Results Count */}
            <div className="mb-6" data-id="element-602">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-603">
                {filteredStorybooks.length !== completedStorybooks.length ? `${filteredStorybooks.length} of ${completedStorybooks.length} Storybooks` : `${completedStorybooks.length} Storybooks`}
              </h2>
            </div>

            {/* Storybooks Grid/List */}
            {viewMode === 'grid' ? <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6" data-id="element-604">
                {filteredStorybooks.map(storybook => {
            const heightClass = storybook.height === 'short' ? 'h-64' : storybook.height === 'tall' ? 'h-96' : 'h-80';
            return <div key={storybook.id} onClick={() => onViewStorybook?.(storybook.id)} data-tour={storybook.id === 1 ? 'storybook-card' : undefined} className="group break-inside-avoid cursor-pointer" data-id="element-605">
                      <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all hover:-translate-y-1" data-id="element-606">
                        {/* Cover Image */}
                        <div className={`relative ${heightClass} overflow-hidden`} data-id="element-607">
                          <img src={storybook.coverImage} alt={storybook.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-608" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-609"></div>

                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-610">
                            <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center" data-id="element-611">
                              <Play className="w-8 h-8 text-gray-900 ml-1" data-id="element-612" />
                            </div>
                          </div>

                          <div className="absolute bottom-4 left-4 right-4" data-id="element-613">
                            <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-lg" data-id="element-614">
                              {storybook.title}
                            </h3>
                            <div className="flex items-center gap-2 flex-wrap" data-id="element-615">
                              <div className="px-2.5 py-1 bg-black/75 text-white text-xs font-medium rounded backdrop-blur-sm" data-id="element-616">
                                {storybook.pages} pages
                              </div>
                              {storybook.views && <div className="px-2.5 py-1 bg-black/75 text-white text-xs font-medium rounded backdrop-blur-sm flex items-center gap-1" data-id="element-617">
                                  <Play className="w-3 h-3" data-id="element-618" />
                                  {storybook.views} views
                                </div>}
                            </div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-5" data-id="element-619">
                          <div className="flex items-center gap-2 text-xs text-gray-500 mb-3" data-id="element-620">
                            <Calendar className="w-3 h-3" data-id="element-621" />
                            <span data-id="element-622">{storybook.date}</span>
                            <span data-id="element-623">•</span>
                            <span data-id="element-624">from "{storybook.storyTitle}"</span>
                          </div>

                          <p className="text-sm text-gray-600 line-clamp-2 mb-4" data-id="element-625">
                            {storybook.summary}
                          </p>

                          <button onClick={e => {
                    e.stopPropagation();
                    onViewStorybook?.(storybook.id);
                  }} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-50 text-purple-600 font-medium rounded-lg hover:bg-purple-100 transition-colors" data-id="element-626">
                            <BookOpen className="w-4 h-4" data-id="element-627" />
                            Read Book
                            <ArrowRight className="w-4 h-4" data-id="element-628" />
                          </button>
                        </div>
                      </div>
                    </div>;
          })}
              </div> : <div className="space-y-4" data-id="element-629">
                {filteredStorybooks.map(storybook => <div key={storybook.id} onClick={() => onViewStorybook?.(storybook.id)} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all cursor-pointer" data-id="element-630">
                    <div className="flex gap-6" data-id="element-631">
                      {/* Thumbnail */}
                      <img src={storybook.coverImage} alt={storybook.title} className="w-32 h-32 rounded-xl object-cover flex-shrink-0" data-id="element-632" />

                      {/* Content */}
                      <div className="flex-1 min-w-0" data-id="element-633">
                        <div className="flex items-start justify-between mb-2" data-id="element-634">
                          <div data-id="element-635">
                            <h3 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-636">
                              {storybook.title}
                            </h3>
                            <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-637">
                              <div className="flex items-center gap-1" data-id="element-638">
                                <Calendar className="w-4 h-4" data-id="element-639" />
                                {storybook.date}
                              </div>
                              <span data-id="element-640">•</span>
                              <span data-id="element-641">{storybook.pages} pages</span>
                              {storybook.views && <>
                                  <span data-id="element-642">•</span>
                                  <div className="flex items-center gap-1" data-id="element-643">
                                    <Play className="w-4 h-4" data-id="element-644" />
                                    {storybook.views} views
                                  </div>
                                </>}
                            </div>
                          </div>
                        </div>

                        <p className="text-gray-700 mb-3 line-clamp-2" data-id="element-645">
                          {storybook.summary}
                        </p>

                        <div className="flex items-center justify-between" data-id="element-646">
                          <span className="text-sm text-gray-500" data-id="element-647">
                            from "{storybook.storyTitle}"
                          </span>
                          <button onClick={e => {
                    e.stopPropagation();
                    onViewStorybook?.(storybook.id);
                  }} className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white font-medium rounded-lg hover:bg-purple-600 transition-colors" data-id="element-648">
                            <BookOpen className="w-4 h-4" data-id="element-649" />
                            Read Book
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>}
          </>}

        {/* Coming Soon States for Other Types */}
        {selectedType !== 'storybook' && <div className="bg-white rounded-3xl p-16 shadow-sm text-center" data-id="element-650">
            <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6" data-id="element-651">
              {selectedType === 'movie' && <Film className="w-12 h-12 text-gray-400" data-id="element-652" />}
              {selectedType === 'image' && <ImageIcon className="w-12 h-12 text-gray-400" data-id="element-653" />}
              {selectedType === 'quest' && <Map className="w-12 h-12 text-gray-400" data-id="element-654" />}
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-655">
              {creationTypes.find(t => t.id === selectedType)?.name} Coming
              Soon
            </h2>
            <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto" data-id="element-656">
              We're working hard to bring you amazing new ways to preserve and
              share your stories.
              {selectedType === 'movie' && ' Create animated videos with AI-generated scenes and voiceovers.'}
              {selectedType === 'image' && ' Generate beautiful single illustrations from your stories.'}
              {selectedType === 'quest' && ' Design interactive adventure journeys with challenges and discoveries.'}
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl" data-id="element-657">
              <Lock className="w-5 h-5" data-id="element-658" />
              <span className="font-medium" data-id="element-659">Notify me when available</span>
            </div>
          </div>}
      </div>
    </div>;
}